import LocationOnIcon from '@mui/icons-material/LocationOn'
import VideoChatIcon from '@mui/icons-material/VideoChat'
import WeekendIcon from '@mui/icons-material/Weekend'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import BusinessIcon from '@mui/icons-material/Business'
import EventIcon from '@mui/icons-material/Event'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { getPeekInForClinic, getPeekInExpiration, addOrUpdatePeekIn, isPeekInExpired } from './utils/peekInManager'
import { peekIntoRoom } from 'src/clinic/components/CheckIn/services'
import { Avatar, Paper, Tooltip, Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import FormControl from '@mui/material/FormControl'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select from '@mui/material/Select'
import { alpha, styled as muiStyled } from '@mui/material/styles'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import capitalize from 'lodash/capitalize'
import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { Spinner } from 'react-bootstrap'
import PastTherapyReportDialog from 'src/clinic/components/ClinicManageBookingsPage/pastTherapyReports/PastTherapyReportDialog'
import { PastTherapyReportDialogPaper } from 'src/clinic/components/ClinicManageBookingsPage/receiptPrinting/styledComponents'
import PaymentLinksViewer from 'src/clinic/components/ClinicManageBookingsPage/slotItem/component/PaymentLinksViewer'
import {
  allAvailableEventTypes,
  BOOKING_PAYMENT_STATUSES,
  BOOKING_SESSION_STATUS,
  BOOKING_STATUSES,
  displayNamesOfEventTypes,
  STATUS_SESSION_CANCELLED,
  VISIT_EVENT_TYPES,
} from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { formatPhoneNumber } from 'src/consumer/helpers/utilFunctions'
import { cancelBookedSession } from 'src/consumer/services/featureServices'
import { getBookedSlotStripColor } from 'src/clinic/components/ClinicManageBookingsPage/utils/bookedSlotColorUtils'
import { getImageLink, isCommonResponseSuccessful, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { GenericDisplayFlex } from 'src/mentor/layout/Styles'
import { getAllRolesInBoolean, getUserRoles } from 'src/mentor/layout/utils'
import { postMPDataWithoutParams } from 'src/mentor/services'
import { toast } from 'react-toastify'
import { useCheckInGuard } from './hooks/useCheckInGuard'
import CheckInDialogComponent from 'src/clinic/components/CheckIn'
import CheckoutPrompt from './components/CheckoutPrompt'
import MentorBookingCommunicationHub from 'src/mentor/components/contact/MentorBookingCommunicationHub'
import ReportViewDrawer from './components/ReportViewDrawer'
import { buildSupportBookedEventActionErrorMessage } from 'src/supportPortal/services/supportBookedEventRescheduleService'
import {
  canViewSupportReportForEventType,
  getSupportReportAccessConfig,
} from 'src/supportPortal/access/accessMatrix'
import SupportReportViewDrawer from 'src/supportPortal/components/bookings/SupportReportViewDrawer'
import {
  CopyEventLinkButton,
  ScheduleName,
} from '../MentorAvailabilityPage/MentorAvailabilityPageStyles'
import {
  BookedEventContainer,
  BookedEventTitle,
  EventsPageItemContainer,
  EventsPageMainItemsContainer,
  StyledEventsRefreshIcon,
} from './MentorEventsPageStyles'
import { formatScreeningSpecialtyLabel } from 'src/consumer/helpers/screeningSpecialtyUtils'
import { therapyDisciplineLabel } from 'src/consumer/constants'

dayjs.extend(utc)
dayjs.extend(isSameOrBefore)

//////////////////////////////////////////////////////////////////////

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

export default function MultipleSelectPanelMembers(props) {
  const { options, selected, handleMultiSelectPanelMembersChange } = props

  return (
    <div>
      <FormControl sx={{ m: 1, width: '100%' }}>
        <Select
          labelId="demo-multiple-checkbox-label"
          id="demo-multiple-checkbox"
          multiple
          value={selected}
          onChange={handleMultiSelectPanelMembersChange}
          input={<OutlinedInput label="Tag" />}
          renderValue={(selected) => {
            if (selected?.length === 0) {
              return <span>No panel members selected</span>
            } else {
              return `${selected?.length} members selected`
            }
          }}
          MenuProps={MenuProps}
        >
          {options?.map((option, index) => (
            <MenuItem key={index} value={option?.id}>
              <Checkbox checked={selected?.indexOf(option?.id) > -1} />
              <ListItemText primary={option?.name} />
              <Avatar src={getImageLink(option?.profilePicId)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )
}

export function MentorEventSummaryComponent({
  onClickEvent,
  item,
  handleCopyEventLink,
  handleOpenBookingPage,
  readOnlyLabel = '',
  helperText = '',
  hideActions = false,
  hideAccessAndPricingDetails = false,
}) {
  // Phase 14bb — show specialty + discipline for any event that has them set, not just
  // screening events. Both fields are now exposed across all clinical event types in
  // the editor, so the listing card mirrors that reality. Specialty resolves to the
  // "Default" sentinel for empty values on screening (preserves the original behaviour);
  // for other event types we only show the line when there's a real, non-default value
  // so we don't clutter cards that haven't opted in. Discipline only renders when the
  // event explicitly carries one — psychotherapy is the implicit default and showing
  // "Discipline: Psychotherapy" on every therapy card would be noise.
  const rawTargetSpecialty = item?.targetSpecialtySlug ?? item?.screeningType
  const targetSpecialtyLabel = rawTargetSpecialty
    ? formatScreeningSpecialtyLabel(rawTargetSpecialty)
    : ''
  const showTargetSpecialty =
    item?.type === 'screening'
      ? Boolean(targetSpecialtyLabel)
      : Boolean(rawTargetSpecialty) // skip the screening-default sentinel for non-screening
  const explicitDiscipline = item?.discipline || item?.therapyDiscipline
  return (
    <EventsPageItemContainer onClick={onClickEvent} enabled={item?.enabled}>
      <ScheduleName main>{item?.title}</ScheduleName>
      <ScheduleName>
        {item?.meetingSrc === 'aumhum' ? <VideoChatIcon /> : <LocationOnIcon />}
        {item?.durationInMins} mins, {displayNamesOfEventTypes?.[item?.type]}
      </ScheduleName>
      {showTargetSpecialty && (
        <ScheduleName>Specialty: {targetSpecialtyLabel}</ScheduleName>
      )}
      {explicitDiscipline && (
        <ScheduleName>Discipline: {therapyDisciplineLabel(explicitDiscipline)}</ScheduleName>
      )}
      {!hideAccessAndPricingDetails && (
        <ScheduleName>
          Buffer: {item?.preBufferMins} mins before, {item?.postBufferMins} mins after
        </ScheduleName>
      )}
      <ScheduleName style={{ color: item?.enabled ? 'green' : 'red' }}>
        {item?.enabled ? 'Enabled' : 'Disabled'}
      </ScheduleName>
      {!hideAccessAndPricingDetails && (
        <ScheduleName style={{ color: item?.publicEvent ? 'green' : 'red' }}>
          {item?.publicEvent ? 'Public' : 'Private'}
        </ScheduleName>
      )}
      {!hideAccessAndPricingDetails && (
        <ScheduleName style={{ color: !item?.free ? 'green' : 'red' }}>
          {item?.free ? 'Free' : 'Paid'}
        </ScheduleName>
      )}
      {readOnlyLabel && <ScheduleName style={{ color: '#9a3412' }}>{readOnlyLabel}</ScheduleName>}
      {helperText && <ScheduleName>{helperText}</ScheduleName>}
      {!hideActions && item?.uid && (
        <GenericDisplayFlex sb>
          <CopyEventLinkButton onClick={(e) => handleCopyEventLink(e, item)}>
            Copy Link
          </CopyEventLinkButton>
          <CopyEventLinkButton invert onClick={(e) => handleOpenBookingPage(e, item)}>
            View Booking Page
          </CopyEventLinkButton>
        </GenericDisplayFlex>
      )}
    </EventsPageItemContainer>
  )
}

export function MentorUpcomingPastEventsTable({
  mentorProfileData,
  allEvents,
  refreshEvents,
  loadingSlots,
  onClickJoin = () => {},
  eventFindLogicOverride = (eventItem, item) => eventItem?.uid === item?.eventTypeId,
  showMentorName = false,
  hideActions = false,
  hideAllActions = false,
  hidePortalVoiceCallAction = false,
  hideRefreshButton = false,
  openBookedEventLogsDialog,
  rightSideCustomComponent,
  bookingCardLayout = 'default',
  renderActionsHeaderTrailing,
  renderActionsSupplement,
  renderManageSessionActionItems,
  renderViewActionItems,
  renderBottomActionGroups,
  onRescheduleBooking,
  cancelAppointmentApiSignature,
  skipCancelCheckInGuard = false,
  onEngagementClick, // Callback for engagement settings (support portal)
  communicationTargetsByBookedEventId = {},
  communicationTargetsLoading = false,
  communicationVisibleTargetKeys = undefined,
  hidePatientContactDetails = false,
}) {
  const [bookedEventsSelectedView, setBookedEventsSelectedView] = useState('upcoming')
  const [currentTime, setCurrentTime] = useState(dayjs().format('YYYY-MM-DD HH:mm:ss'))
  const [cancelDialogData, setCancelDialogData] = useState(null)
  const [selectedBooking, setSelectedBooking] = useState(false)
  const [showPaymentLinksDialog, setShowPaymentLinksDialog] = useState(false)
  const [pastReportsDialog, setShowPastReports] = useState(false)
  const [reportDrawerState, setReportDrawerState] = useState({
    open: false,
    bookedEvent: null,
    reportType: null,
  })
  const isSupportBookingCardLayout = bookingCardLayout === 'support'

  // const upcomingBookedEvents = allEvents?.filter((item) =>
  //   dayjs?.utc(item?.endTime)?.local()?.isSameOrAfter(dayjs?.(currentTime)),
  // )
  // const pastBookedEvents = allEvents
  //   ?.filter((item) => dayjs?.utc(item?.endTime)?.local()?.isBefore(dayjs?.(currentTime)))
  //   ?.reverse()

  function openPaymentLinkPopup(item) {
    setSelectedBooking(item)
    setShowPaymentLinksDialog(true)
  }
  function closePaymentLinkPopup() {
    setSelectedBooking(null)
    setShowPaymentLinksDialog(false)
  }
  function openPastReportsDialog(item) {
    setSelectedBooking(item)
    setShowPastReports(true)
  }
  function closePastReportsDialog() {
    setShowPastReports(false)
    setSelectedBooking(null)
  }

  const openReportDrawer = useCallback((bookedEvent, reportType) => {
    if (!bookedEvent || !reportType) {
      return
    }

    setReportDrawerState({
      open: true,
      bookedEvent,
      reportType,
    })
  }, [])

  const closeReportDrawer = useCallback(() => {
    setReportDrawerState((currentValue) => ({
      ...currentValue,
      open: false,
    }))
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(dayjs().format('YYYY-MM-DD HH:mm:ss'))
    }, 60000)

    return () => clearInterval(interval)
  }, [])

  // Component to show check-in/peek-in status on card
  function CheckInStatusIndicator({ bookedEvent }) {
    const checkInGuard = useCheckInGuard(bookedEvent, {
      mentorProfileData,
      mentorClinicAssociationData: null, // Not needed for support portal
    })
    const { isCheckedInOrPeekedIn, handleCheckout } = checkInGuard
    const [expirationUpdateTime, setExpirationUpdateTime] = useState(Date.now())

    // Check if peeked in (with expiration)
    const peekIn = isCheckedInOrPeekedIn?.isPeekedIn
      ? getPeekInForClinic(bookedEvent?.clinicId)
      : null
    const peekInExpiration = peekIn ? getPeekInExpiration(peekIn) : null

    // Update expiration display every minute
    useEffect(() => {
      if (peekInExpiration) {
        const interval = setInterval(() => {
          setExpirationUpdateTime(Date.now())
        }, 60000) // Every minute

        return () => clearInterval(interval)
      }
    }, [peekInExpiration])

    if (!isCheckedInOrPeekedIn) return null
    
    // Show checked-in status
    if (isCheckedInOrPeekedIn.isCheckedIn) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 0.5,
          p: 0.75,
          bgcolor: '#10b98110',
          borderRadius: '8px',
          border: '1px solid #10b98130'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#10b981' }}>
              Checked In
            </Typography>
          </Box>
          <Tooltip title="Check Out">
            <IconButton
              size="small"
              onClick={() => handleCheckout()}
              sx={{
                color: '#ef4444',
                '&:hover': { bgcolor: '#ef444410' },
                p: 0.5,
              }}
            >
              <ExitToAppIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }

    // Show peek-in status with expiration
    if (isCheckedInOrPeekedIn.isPeekedIn && peekIn) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 0.5,
          p: 0.75,
          bgcolor: '#6366f110',
          borderRadius: '8px',
          border: '1px solid #6366f130'
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <VisibilityIcon sx={{ fontSize: 18, color: '#6366f1' }} />
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#6366f1' }}>
                Peeked In
              </Typography>
            </Box>
            {peekInExpiration && (
              <Typography 
                sx={{ fontSize: '11px', color: '#6366f1', ml: 3.5 }}
                key={expirationUpdateTime} // Force re-render on update
              >
                Expires at {dayjs(peekInExpiration).local().format('h:mm A')}
              </Typography>
            )}
          </Box>
          <Tooltip title="Exit Peek-in">
            <IconButton
              size="small"
              onClick={() => handleCheckout()}
              sx={{
                color: '#ef4444',
                '&:hover': { bgcolor: '#ef444410' },
                p: 0.5,
              }}
            >
              <ExitToAppIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }

    return null
  }

  function BookingFactRow({ icon, label, children }) {
    return (
      <Stack
        direction="row"
        spacing={0.85}
        alignItems="flex-start"
        sx={{
          minWidth: 0,
          height: '100%',
          p: 0.95,
          borderRadius: '8px',
          border: '1px solid rgba(203, 213, 225, 0.92)',
          background: 'rgba(255, 255, 255, 0.9)',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.72)',
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(241, 245, 249, 0.92)',
            color: '#475569',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: '#64748b',
              fontWeight: 800,
              letterSpacing: 0,
              textTransform: 'uppercase',
              lineHeight: 1.2,
              mb: 0.3,
            }}
          >
            {label}
          </Typography>
          {children}
        </Box>
      </Stack>
    )
  }

  function SessionTimeRail({
    dayLabel,
    datePrimaryLabel,
    dateSecondaryLabel,
    startLabel,
    endLabel,
    statusLabel,
    statusHelperLabel,
    MeetingModeIcon,
    colors,
    isCancelled = false,
  }) {
    const railColor = colors?.borderColor || '#4f46e5'
    const railAccentColor = colors?.leftStripBorderColor || railColor

    return (
      <Box
        sx={{
          height: '100%',
          minHeight: { xs: 154, md: 176 },
          borderRadius: '8px',
          p: { xs: 0.75, sm: 0.9 },
          color: '#fff',
          background: `linear-gradient(180deg, ${railColor} 0%, ${railAccentColor} 100%)`,
          boxShadow: isCancelled
            ? 'inset 0 0 0 1px rgba(255, 255, 255, 0.28), 0 10px 24px rgba(185, 28, 28, 0.22)'
            : 'inset 0 0 0 1px rgba(255, 255, 255, 0.16)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 0.8, sm: 0.95 },
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Stack spacing={0.55} alignItems="center" sx={{ width: '100%' }}>
          <Stack spacing={0.2} alignItems="center" sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, lineHeight: 1, textTransform: 'uppercase' }}>
              {dayLabel}
            </Typography>
            <Typography
              sx={{
                fontSize:
                  datePrimaryLabel?.length > 7
                    ? { xs: 14, sm: 17 }
                    : datePrimaryLabel?.length > 2
                    ? { xs: 17, sm: 20 }
                    : { xs: 28, sm: 34 },
                fontWeight: 900,
                lineHeight: 1.05,
                maxWidth: '100%',
                overflowWrap: 'break-word',
              }}
            >
              {datePrimaryLabel}
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 800, lineHeight: 1.2 }}>
              {dateSecondaryLabel}
            </Typography>
          </Stack>

          <Stack spacing={0.25} alignItems="center" sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 900, lineHeight: 1.1 }}>
              {startLabel}
            </Typography>
            {endLabel ? (
              <Typography sx={{ fontSize: 11, fontWeight: 700, opacity: 0.86, lineHeight: 1.2 }}>
                to {endLabel}
              </Typography>
            ) : null}
          </Stack>
        </Stack>

        <Box
          sx={{
            width: '100%',
            px: 0.7,
            py: isCancelled ? 0.95 : 0.75,
            borderRadius: '8px',
            background: isCancelled ? 'rgba(127, 29, 29, 0.56)' : 'rgba(15, 23, 42, 0.26)',
            border: isCancelled
              ? '1px solid rgba(255, 255, 255, 0.34)'
              : '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: isCancelled ? '0 0 0 1px rgba(127, 29, 29, 0.12)' : 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 11, sm: 12 },
              fontWeight: 950,
              lineHeight: 1.15,
              textTransform: 'uppercase',
              overflowWrap: 'break-word',
            }}
          >
            {statusLabel}
          </Typography>
          {statusHelperLabel ? (
            <Typography sx={{ mt: 0.25, fontSize: 10, fontWeight: 800, lineHeight: 1.15, opacity: 0.9 }}>
              {statusHelperLabel}
            </Typography>
          ) : null}
        </Box>

        {MeetingModeIcon ? (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(255, 255, 255, 0.18)',
              border: '1px solid rgba(255, 255, 255, 0.22)',
              color: '#fff',
              flexShrink: 0,
            }}
          >
            <MeetingModeIcon sx={{ fontSize: 21 }} />
          </Box>
        ) : null}
      </Box>
    )
  }

  function BookingActionButton({
    label,
    onClick,
    color = 'inherit',
    variant = 'outlined',
    style = {},
    disabled = false,
    title = '',
    primary = false,
    startIcon = null,
    tone = 'default',
    gridColumn = 'auto',
  }) {
    const toneStyles = {
      default: {
        color: '#334155',
        borderColor: 'rgba(100, 116, 139, 0.32)',
        backgroundColor: '#ffffff',
      },
      report: {
        color: '#334155',
        borderColor: 'rgba(100, 116, 139, 0.3)',
        backgroundColor: '#ffffff',
      },
      history: {
        color: '#334155',
        borderColor: 'rgba(100, 116, 139, 0.3)',
        backgroundColor: '#ffffff',
      },
      schedule: {
        color: '#334155',
        borderColor: 'rgba(100, 116, 139, 0.3)',
        backgroundColor: '#ffffff',
      },
      logs: {
        color: '#475569',
        borderColor: 'rgba(71, 85, 105, 0.28)',
        backgroundColor: 'rgba(71, 85, 105, 0.07)',
      },
      danger: {
        color: '#b91c1c',
        borderColor: 'rgba(220, 38, 38, 0.32)',
        backgroundColor: 'rgba(254, 226, 226, 0.72)',
      },
      payment: {
        color: '#334155',
        borderColor: 'rgba(100, 116, 139, 0.3)',
        backgroundColor: '#ffffff',
      },
      engagement: {
        color: '#6d28d9',
        borderColor: 'rgba(124, 58, 237, 0.3)',
        backgroundColor: 'rgba(124, 58, 237, 0.08)',
      },
    }
    const appliedToneStyle = primary ? {} : toneStyles[tone] || toneStyles.default
    const button = (
      <Button
        size="small"
        variant={variant}
        color={color}
        disabled={disabled}
        startIcon={startIcon}
        onClick={(event) => {
          event.stopPropagation()
          if (!disabled) {
            onClick?.(event)
          }
        }}
        sx={{
          gridColumn,
          borderRadius: '18px',
          textTransform: 'none',
          fontWeight: primary ? 900 : 750,
          minHeight: primary ? 44 : 36,
          px: primary ? 1.6 : 1.35,
          width: '100%',
          justifyContent: 'center',
          whiteSpace: 'normal',
          lineHeight: 1.18,
          boxShadow:
            primary && variant === 'contained'
              ? '0 8px 18px rgba(37, 99, 235, 0.18)'
              : '0 1px 2px rgba(15, 23, 42, 0.08)',
          '&:hover': {
            boxShadow:
              primary && variant === 'contained'
                ? '0 10px 22px rgba(37, 99, 235, 0.22)'
                : '0 5px 12px rgba(15, 23, 42, 0.12)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.Mui-disabled': {
            color: '#64748b',
            borderColor: 'rgba(148, 163, 184, 0.5)',
            backgroundColor: 'rgba(148, 163, 184, 0.14)',
            boxShadow: 'none',
          },
          ...appliedToneStyle,
          ...style,
        }}
      >
        {label}
      </Button>
    )

    if (!title) {
      return button
    }

    return (
      <Tooltip title={title}>
        <Box component="span" sx={{ display: 'block', gridColumn }}>
          {button}
        </Box>
      </Tooltip>
    )
  }

  function BookingActionGroup({
    title,
    children,
    columns = 1,
    tone = 'default',
    minActionWidth = 132,
  }) {
    const childItems = (Array.isArray(children) ? children : [children]).filter(Boolean)

    if (childItems.length === 0) {
      return null
    }

    const toneStyles = {
      default: {
        border: '1px solid rgba(148, 163, 184, 0.28)',
        background: 'rgba(248, 250, 252, 0.68)',
      },
      primary: {
        border: '1px solid rgba(37, 99, 235, 0.2)',
        background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.96) 0%, rgba(255, 255, 255, 0.94) 100%)',
      },
      view: {
        border: '1px solid rgba(148, 163, 184, 0.28)',
        background: 'rgba(248, 250, 252, 0.58)',
      },
      manage: {
        border: '1px solid rgba(148, 163, 184, 0.28)',
        background: 'rgba(248, 250, 252, 0.58)',
      },
    }

    return (
      <Box
        sx={{
          borderRadius: '8px',
          p: 0.85,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.65,
          ...(toneStyles[tone] || toneStyles.default),
        }}
      >
        {title ? (
          <Typography
            variant="caption"
            sx={{ color: '#475569', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0 }}
          >
            {title}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns:
              columns === 'auto'
                ? `repeat(auto-fit, minmax(min(${minActionWidth}px, 100%), 1fr))`
                : columns > 1
                ? `repeat(${columns}, minmax(0, 1fr))`
                : '1fr',
            gap: 0.65,
          }}
        >
          {childItems}
        </Box>
      </Box>
    )
  }

  function BookingActionLink({ label, onClick, disabled = false, title = '' }) {
    const link = (
      <Typography
        component="button"
        type="button"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation()
          if (!disabled) {
            onClick?.(event)
          }
        }}
        sx={{
          gridColumn: '1 / -1',
          appearance: 'none',
          border: 0,
          background: 'transparent',
          p: 0.25,
          m: 0,
          color: disabled ? '#94a3b8' : '#475569',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: 12,
          fontWeight: 850,
          lineHeight: 1.25,
          textAlign: 'left',
          textDecoration: disabled ? 'none' : 'underline',
          textUnderlineOffset: '3px',
          '&:hover': {
            color: disabled ? '#94a3b8' : '#334155',
          },
        }}
      >
        {label}
      </Typography>
    )

    if (!title) {
      return link
    }

    return (
      <Tooltip title={title}>
        <Box component="span" sx={{ gridColumn: '1 / -1' }}>
          {link}
        </Box>
      </Tooltip>
    )
  }

  function BookedEventActions({ bookedEvent }) {
    const checkInGuard = useCheckInGuard(bookedEvent, {
      mentorProfileData,
      mentorClinicAssociationData: null, // Not needed for support portal
    })
    const {
      checkInPromptOpen,
      checkoutPromptOpen,
      checkingIn,
      availableRooms,
      clinicLocationData,
      requireCheckIn,
      handleCheckout,
      setCheckInPromptOpen,
      setCheckoutPromptOpen,
      isCheckedInOrPeekedIn,
    } = checkInGuard

    const [supportReportDrawerOpen, setSupportReportDrawerOpen] = useState(false)
    // `isSupportPortal` MUST be declared before `shouldRequestReschedule`
    // and `shouldRequestCancel` below — both reference it inline. The
    // earlier ordering put isSupportPortal near the bottom of the
    // hook body and the references near the top, which raised the
    // production TDZ error
    //   "Cannot access 'sr' before initialization" (sr = minified
    //   shouldRequestReschedule)
    // on the /mentor-home/manage-my-bookings page. Dev usually masks
    // these — the prod minifier hoists differently and surfaces the
    // dead-zone access.
    const isSupportPortal = useMemo(() => {
      if (typeof window === 'undefined') {
        return false
      }
      const pathname = window.location?.pathname || ''
      return pathname.startsWith('/support-home') || pathname.startsWith('/support/')
    }, [])
    const shouldRequestReschedule = !isSupportPortal && mentorProfileData?.allowSelfReschedule === false
    const shouldRequestCancel = !isSupportPortal && mentorProfileData?.allowSelfCancel === false
    const rescheduleActionLabel = shouldRequestReschedule ? 'Request Reschedule' : 'Reschedule'
    const cancelActionLabel = shouldRequestCancel ? 'Request Cancel' : 'Cancel'

    // Store the room data when dialog opens to prevent unmounting during refetch
    const [dialogRoomData, setDialogRoomData] = useState(null)

    // Update dialogRoomData when dialog opens and we have rooms
    useEffect(() => {
      if (checkInPromptOpen && availableRooms.length > 0 && clinicLocationData) {
        // Store the first available room to prevent unmounting during refetch
        setDialogRoomData(availableRooms[0])
      } else if (!checkInPromptOpen) {
        // Clear when dialog closes
        setDialogRoomData(null)
      }
    }, [checkInPromptOpen, availableRooms, clinicLocationData])

    // Check if session has started (startTime is in the past or now)
    const hasSessionStarted = useMemo(() => {
      if (!bookedEvent?.startTime) return false
      const sessionStartTime = dayjs.utc(bookedEvent.startTime).local()
      const now = dayjs(currentTime)
      return sessionStartTime.isSameOrBefore(now)
    }, [bookedEvent?.startTime, currentTime])

    const currentUserRoles = useMemo(() => getUserRoles() || [], [])
    const canViewSupportReport = useMemo(
      () =>
        isSupportPortal &&
        canViewSupportReportForEventType(currentUserRoles, bookedEvent?.eventType),
      [bookedEvent?.eventType, currentUserRoles, isSupportPortal],
    )
    const supportReportConfig = useMemo(
      () => (isSupportPortal ? getSupportReportAccessConfig(bookedEvent?.eventType) : null),
      [bookedEvent?.eventType, isSupportPortal],
    )

    // Helper function to do silent peek-in for video calls
    const doSilentPeekInForVideoCall = async (onSuccess) => {
      if (!clinicLocationData) {
        requireCheckIn(onSuccess)
        return
      }
      
      // Check if already peeked in
      const existingPeekIn = getPeekInForClinic(clinicLocationData.id)
      if (existingPeekIn && !isPeekInExpired(existingPeekIn)) {
        // Already peeked in and not expired, proceed directly
        onSuccess()
        return
      }
      
      // Get clinic location short name and slug
      const clinicShortName = clinicLocationData?.shortName || 
                              clinicLocationData?.short_name || 
                              clinicLocationData?.nickName || 
                              clinicLocationData?.nickname ||
                              clinicLocationData?.name ||
                              'Clinic'
      
      const clinicSlug = clinicLocationData?.slug || 
                        clinicLocationData?.clinicSlug ||
                        clinicLocationData?.id?.toString() ||
                        'clinic'
      
      // Create custom video call room
      // Note: peek-in API doesn't require selectedCheckInEventOption
      const videoCallRoom = {
        id: `${clinicSlug}--video-call-room`,
        title: `${clinicShortName}: Video Call Room`,
        name: `${clinicShortName}: Video Call Room`,
        clinicLocationId: clinicLocationData.id,
        clinicBrandId: clinicLocationData.clinicBrandId || bookedEvent?.clinicBrandId,
      }
      
      // Do silent peek-in
      try {
        const response = await peekIntoRoom(videoCallRoom)
        if (response?.status === 200) {
          // Add to peek-in manager with 1 hour expiration
          addOrUpdatePeekIn(videoCallRoom)
          // Proceed with the action
          onSuccess()
        } else {
          console.error('Peek-in API failed:', response)
          // If peek-in fails, fall back to normal check-in flow
          requireCheckIn(onSuccess)
        }
      } catch (error) {
        console.error('Error doing silent peek-in:', error)
        // If peek-in fails, fall back to normal check-in flow
        requireCheckIn(onSuccess)
      }
    }

    // Helper function to do silent peek-in for editing reports
    // For offline sessions: uses <Clinic-Location-Slug>--Editing-Room
    // For video call sessions: uses <Clinic Location Slug>--video-call-room
    const doSilentPeekInForEditing = async (onSuccess) => {
      if (!clinicLocationData) {
        requireCheckIn(onSuccess)
        return
      }
      
      // Check if already peeked in
      const existingPeekIn = getPeekInForClinic(clinicLocationData.id)
      if (existingPeekIn && !isPeekInExpired(existingPeekIn)) {
        // Already peeked in and not expired, proceed directly
        onSuccess()
        return
      }
      
      // Get clinic location short name and slug
      const clinicShortName = clinicLocationData?.shortName || 
                              clinicLocationData?.short_name || 
                              clinicLocationData?.nickName || 
                              clinicLocationData?.nickname ||
                              clinicLocationData?.name ||
                              'Clinic'
      
      const clinicSlug = clinicLocationData?.slug || 
                        clinicLocationData?.clinicSlug ||
                        clinicLocationData?.id?.toString() ||
                        'clinic'
      
      const isVideoCall = bookedEvent?.meetingSrc === 'aumhum'
      
      // Determine room ID based on session type
      let roomId, roomTitle, roomName
      if (isVideoCall) {
        // Video call: use video-call-room
        roomId = `${clinicSlug}--video-call-room`
        roomTitle = `${clinicShortName}: Video Call Room`
        roomName = `${clinicShortName}: Video Call Room`
      } else {
        // Offline: use Editing-Room
        roomId = `${clinicSlug}--Editing-Room`
        roomTitle = `${clinicShortName}: Editing Room`
        roomName = `${clinicShortName}: Editing Room`
      }
      
      // Create custom room for editing
      const editingRoom = {
        id: roomId,
        title: roomTitle,
        name: roomName,
        clinicLocationId: clinicLocationData.id,
        clinicBrandId: clinicLocationData.clinicBrandId || bookedEvent?.clinicBrandId,
      }
      
      // Do silent peek-in
      try {
        const response = await peekIntoRoom(editingRoom)
        if (response?.status === 200) {
          // Add to peek-in manager with 1 hour expiration
          addOrUpdatePeekIn(editingRoom)
          // Proceed with the action
          onSuccess()
        } else {
          console.error('Peek-in API failed:', response)
          // If peek-in fails, fall back to normal check-in flow
          requireCheckIn(onSuccess)
        }
      } catch (error) {
        console.error('Error doing silent peek-in:', error)
        // If peek-in fails, fall back to normal check-in flow
        requireCheckIn(onSuccess)
      }
    }

    // Wrapper functions that require check-in
    const handlePrescription = () => {
      // Use silent peek-in with appropriate room based on session type
      doSilentPeekInForEditing(() => {
        openReportDrawer(bookedEvent, 'prescription')
      })
    }

    const handleScreeningReport = () => {
      // Use silent peek-in with appropriate room based on session type
      doSilentPeekInForEditing(() => {
        openReportDrawer(bookedEvent, 'screening-report')
      })
    }

    const handleTherapyReport = () => {
      if (!hasSessionStarted) {
        toast.info('This session has not started yet, so no therapy report is available.')
        return
      }

      // Use silent peek-in with appropriate room based on session type
      doSilentPeekInForEditing(() => {
        openReportDrawer(bookedEvent, 'therapy-report')
      })
    }

    const handlePatientHistory = () => {
      // Use silent peek-in with appropriate room based on session type
      doSilentPeekInForEditing(() => {
        openPastReportsDialog(bookedEvent)
      })
    }

    const handleStart = async () => {
      const isVideoCall = bookedEvent?.meetingSrc === 'aumhum'
      
      // For video calls, do silent peek-in instead of showing check-in popup
      if (isVideoCall && clinicLocationData) {
        // Use the helper function for silent peek-in
        await doSilentPeekInForVideoCall(() => {
          onClickJoin(bookedEvent)
        })
        return
      }
      
      // For in-person (offline) sessions, show check-in prompt even if peeked-in
      const isInPersonSession = !isVideoCall
      const isOnlyPeekedIn = isCheckedInOrPeekedIn?.isPeekedIn && !isCheckedInOrPeekedIn?.isCheckedIn
      
      // Force check-in prompt for in-person sessions when only peeked-in
      const forceCheckIn = isInPersonSession && isOnlyPeekedIn
      
      requireCheckInWithAction(() => {
        onClickJoin(bookedEvent)
      }, forceCheckIn)
    }

    const handleCancel = () => {
      if (shouldRequestCancel) {
        const reason = window.prompt('Reason for cancellation request (optional):')
        if (reason === null) return
        postMPDataWithoutParams('/api/secure/mentor/booked-events/cancel-request', {
          bookedEventId: bookedEvent.id,
          bookedEventSlug: bookedEvent.slug,
          reason,
          refundType: 'none',
        })
          .then((response) => {
            if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
              toast.success('Cancellation request sent to support')
              if (refreshEvents) refreshEvents()
            } else {
              toast.error(response?.data?.message || 'Could not send cancellation request')
            }
          })
          .catch(() => toast.error('Could not send cancellation request'))
        return
      }

      if (skipCancelCheckInGuard) {
        setCancelDialogData(bookedEvent)
        return
      }

      const isVideoCall = bookedEvent?.meetingSrc === 'aumhum'
      if (isVideoCall) {
        doSilentPeekInForVideoCall(() => {
          setCancelDialogData(bookedEvent)
        })
      } else {
        requireCheckIn(() => {
          setCancelDialogData(bookedEvent)
        })
      }
    }

    const handleReschedule = () => {
      if (onRescheduleBooking && !shouldRequestReschedule) {
        onRescheduleBooking(bookedEvent)
        return
      }

      const isVideoCall = bookedEvent?.meetingSrc === 'aumhum'
      const rescheduleAction = async () => {
        // Open a simple prompt for reschedule date/time
        const newDateStr = window.prompt('Enter new date and time (YYYY-MM-DD HH:mm):')
        if (!newDateStr) return

        try {
          const newStartTime = dayjs(newDateStr).utc().toISOString()
          const payload = {
            bookedEventId: bookedEvent.id,
            newStartTime: newStartTime,
            notifyPatient: true,
            reason: 'Rescheduling appointment',
            forceReschedule: false,
            reschedulePolicy: 'DONT_BOOK_IF_CONFLICT',
          }

          const response = await postMPDataWithoutParams(
            shouldRequestReschedule
              ? '/api/secure/mentor/booked-events/reschedule-request'
              : '/api/secure/mentor/booked-events/reschedule',
            payload
          )

          if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
            toast.success(
              shouldRequestReschedule
                ? 'Reschedule request sent to support'
                : 'Booking rescheduled successfully',
            )
            if (refreshEvents) {
              refreshEvents()
            }
          } else {
            const errorCode = response?.data?.code
            let errorMessage = response?.data?.message || 'Failed to reschedule booking'
            
            if (errorCode === 4443) {
              errorMessage = 'Booking already marked completed. Rescheduling is not permitted.'
            } else if (errorCode === 4444) {
              errorMessage = 'Requested time is in the past. Please select a future time.'
            } else if (errorCode === 4448 || errorCode === 4449) {
              errorMessage = 'Time slot conflicts with another booking. Please select a different time or use force reschedule.'
            } else if (errorCode === 48980) {
              errorMessage = 'Self-reschedule is disabled for this clinic. Please contact support.'
            }
            
            toast.error(errorMessage)
          }
        } catch (error) {
          console.error('Error rescheduling booking:', error)
          toast.error('Failed to reschedule booking. Please try again.')
        }
      }
      
      if (isVideoCall) {
        doSilentPeekInForVideoCall(rescheduleAction)
      } else {
        requireCheckIn(rescheduleAction)
      }
    }

    const handleBookingLogs = () => {
      // Use silent peek-in with appropriate room based on session type
      doSilentPeekInForEditing(() => {
        openBookedEventLogsDialog(bookedEvent)
      })
    }

    const handlePaymentLinks = () => {
      requireCheckIn(() => {
        openPaymentLinkPopup(bookedEvent)
      })
    }

    // Store pending action to execute after check-in completes
    const pendingActionAfterCheckInRef = useRef(null)
    
    // Wrapper for requireCheckIn that stores the action
    const requireCheckInWithAction = useCallback(
      async (actionCallback, forceCheckIn = false) => {
        // Store the action to execute after check-in
        pendingActionAfterCheckInRef.current = actionCallback
        // Call the original requireCheckIn (it will show the dialog)
        requireCheckIn(actionCallback, forceCheckIn)
      },
      [requireCheckIn]
    )
    
    // Execute pending action after check-in
    const executePendingAction = useCallback(() => {
      if (pendingActionAfterCheckInRef.current) {
        const action = pendingActionAfterCheckInRef.current
        pendingActionAfterCheckInRef.current = null
        // Small delay to ensure check-in is fully processed
        setTimeout(() => {
          action()
        }, 100)
      }
    }, [])

    // Check if session is blocked (payment pending - slot held but not confirmed)
    const isSessionBlocked = 
      bookedEvent?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
      bookedEvent?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
    const isCompletedForActions = isBookedEventCompletedForActions(bookedEvent)
    const isDoctorConsultation =
      getAllRolesInBoolean().isDoctor &&
      bookedEvent?.eventType === allAvailableEventTypes.consultation
    const canManageCompletedSession = isSupportPortal || isDoctorConsultation
    const canShowPrimaryAction =
      !hideActions && bookedEvent?.status !== STATUS_SESSION_CANCELLED && !isSessionBlocked
    const canShowClinicalViewActions =
      !hideActions &&
      bookedEvent?.status !== STATUS_SESSION_CANCELLED &&
      !isSupportPortal &&
      bookedEvent?.paymentStatus === BOOKING_PAYMENT_STATUSES.PAID
    const canShowPrescription =
      canShowClinicalViewActions &&
      hasSessionStarted &&
      bookedEvent?.patientId &&
      getAllRolesInBoolean().isDoctor &&
      mentorProfileData?.allowedToPrescribe
    const canShowScreeningReport =
      canShowClinicalViewActions &&
      hasSessionStarted &&
      bookedEvent?.patientId &&
      bookedEvent?.eventType === allAvailableEventTypes.screening
    const canShowTherapyReport =
      canShowClinicalViewActions &&
      bookedEvent?.patientId &&
      bookedEvent?.eventType === allAvailableEventTypes.therapySession
    const canShowPatientHistory = canShowClinicalViewActions && bookedEvent?.patientId
    const canShowManageActions =
      !hideActions &&
      bookedEvent?.status !== STATUS_SESSION_CANCELLED &&
      (!isCompletedForActions || canManageCompletedSession)
    const canShowBookingLogs =
      bookedEvent?.status === BOOKING_STATUSES.BOOKED &&
      bookedEvent?.paymentStatus === BOOKING_PAYMENT_STATUSES.PAID &&
      hasSessionStarted
    const canShowPaymentLinks =
      (bookedEvent?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
        bookedEvent?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING) ||
      bookedEvent?.paymentLinks?.length > 0
    const canShowEngagement =
      onEngagementClick && bookedEvent?.status === BOOKING_STATUSES.BOOKED && !isCompletedForActions
    const canShowSupportEngagementInView = canShowEngagement && isSupportPortal
    const canShowBottomEngagement = canShowEngagement && !isSupportPortal
    const canShowSupportReport = canShowPrimaryAction && isSupportPortal && canViewSupportReport
    const supportReportActionLabel = supportReportConfig?.title || 'Report'
    const customViewActionItems = (
      typeof renderViewActionItems === 'function'
        ? renderViewActionItems(bookedEvent, {
            BookingActionGroup,
            BookingActionButton,
            BookingActionLink,
          })
        : null
    )
    const customViewActionItemList = (Array.isArray(customViewActionItems)
      ? customViewActionItems
      : [customViewActionItems]
    ).filter(Boolean)
    const hasViewActions =
      canShowSupportReport ||
      canShowPrescription ||
      canShowScreeningReport ||
      canShowTherapyReport ||
      canShowPatientHistory ||
      (canShowPaymentLinks && !isSessionBlocked) ||
      canShowBookingLogs ||
      canShowSupportEngagementInView ||
      customViewActionItemList.length > 0
    const customActionsHeaderTrailing =
      typeof renderActionsHeaderTrailing === 'function'
        ? renderActionsHeaderTrailing(bookedEvent)
        : null
    const customActionsSupplement =
      typeof renderActionsSupplement === 'function'
        ? renderActionsSupplement(bookedEvent)
        : null
    const customManageSessionActionItems = (
      typeof renderManageSessionActionItems === 'function'
        ? renderManageSessionActionItems(bookedEvent, {
            BookingActionGroup,
            BookingActionButton,
            BookingActionLink,
          })
        : null
    )
    const customManageSessionActionItemList = (Array.isArray(customManageSessionActionItems)
      ? customManageSessionActionItems
      : [customManageSessionActionItems]
    ).filter(Boolean)
    const hasManageSessionActions =
      canShowManageActions ||
      canShowBottomEngagement ||
      customManageSessionActionItemList.length > 0
    const hasBottomManageSessionActions = !isSupportPortal && hasManageSessionActions
    const customBottomActionGroups =
      typeof renderBottomActionGroups === 'function'
        ? renderBottomActionGroups(bookedEvent, {
            BookingActionGroup,
            BookingActionButton,
            BookingActionLink,
          })
        : null
    const customBottomActionItems = (Array.isArray(customBottomActionGroups)
      ? customBottomActionGroups
      : [customBottomActionGroups]
    ).filter(Boolean)

    return (
      <>
        <Box
          sx={{
            gridColumn: { xs: '1 / -1', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            gap: 0.85,
            minWidth: 0,
            borderRadius: '8px',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.94) 0%, rgba(255, 255, 255, 0.98) 100%)',
            p: { xs: 1, sm: 1.05 },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography
              variant="caption"
              sx={{ color: '#475569', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0 }}
            >
              Actions
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" sx={{ minWidth: 0 }}>
              {bookedEvent?.id && !hidePortalVoiceCallAction ? (
                <MentorBookingCommunicationHub
                  bookedEvent={bookedEvent}
                  prefetchedTargets={communicationTargetsByBookedEventId?.[bookedEvent?.id] || null}
                  prefetchedTargetsLoading={
                    communicationTargetsLoading && !communicationTargetsByBookedEventId?.[bookedEvent?.id]
                  }
                  compactPatientActions
                  quietCompactPatientActions
                  sx={{
                    px: 0.45,
                    py: 0.3,
                    borderRadius: 999,
                    border: '1px solid rgba(96, 165, 250, 0.22)',
                    background:
                      'linear-gradient(135deg, rgba(239, 246, 255, 0.94) 0%, rgba(240, 253, 250, 0.9) 100%)',
                  }}
                />
              ) : null}
              {bookedEvent?.id && !hidePortalVoiceCallAction ? (
                <MentorBookingCommunicationHub
                  bookedEvent={bookedEvent}
                  prefetchedTargets={communicationTargetsByBookedEventId?.[bookedEvent?.id] || null}
                  prefetchedTargetsLoading={
                    communicationTargetsLoading && !communicationTargetsByBookedEventId?.[bookedEvent?.id]
                  }
                  visibleTargetKeys={communicationVisibleTargetKeys}
                  supportPopoverOnly
                />
              ) : null}
              {customActionsHeaderTrailing}
            </Stack>
          </Stack>
          <CheckInStatusIndicator bookedEvent={bookedEvent} />
          <BookingActionGroup
            title={isSupportPortal ? 'Manage Session' : 'Session'}
            columns={isSupportPortal ? 2 : 1}
            tone="primary"
          >
            {canShowManageActions && isSupportPortal ? (
              <BookingActionButton label={rescheduleActionLabel} onClick={handleReschedule} tone="schedule" />
            ) : null}
            {canShowManageActions && isSupportPortal ? (
              <BookingActionButton label={cancelActionLabel} onClick={handleCancel} color="error" tone="danger" />
            ) : null}
            {customManageSessionActionItemList}
            {canShowPrimaryAction && !isSupportPortal ? (
              <BookingActionButton
                label="Start/Join Session"
                onClick={handleStart}
                variant="contained"
                color="primary"
                primary
                startIcon={
                  bookedEvent?.meetingSrc === 'aumhum' ? (
                    <VideoChatIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <WeekendIcon sx={{ fontSize: 18 }} />
                  )
                }
              />
            ) : null}
            {isSessionBlocked ? (
              <>
                <Chip
                  label="Awaiting Payment"
                  size="small"
                  sx={{
                    gridColumn: '1 / -1',
                    width: '100%',
                    bgcolor: '#fef3c7',
                    color: '#92400e',
                    fontWeight: 800,
                  }}
                />
                {canShowPaymentLinks ? (
                  <BookingActionButton
                    label="Payment Links"
                    onClick={handlePaymentLinks}
                    variant="contained"
                    color="warning"
                    primary
                    gridColumn={isSupportPortal ? '1 / -1' : 'auto'}
                  />
                ) : null}
              </>
            ) : null}
            {bookedEvent?.status === STATUS_SESSION_CANCELLED ? (
              <Chip
                label="Cancelled"
                size="small"
                color="error"
                variant="outlined"
                sx={{ gridColumn: '1 / -1', width: '100%' }}
              />
            ) : null}
            {!isSupportPortal &&
            bookedEvent?.modificationHistory?.length > 0 &&
            bookedEvent?.modificationHistory?.some((hist) => {
              return hist?.event === 'booking-rescheduled'
            }) ? (
              <Chip label="Rescheduled" size="small" color="info" variant="outlined" sx={{ width: '100%' }} />
            ) : null}
          </BookingActionGroup>
          {customActionsSupplement}
        </Box>

        {hasViewActions || hasBottomManageSessionActions || customBottomActionItems.length > 0 ? (
          <Box
            sx={{
              gridColumn: { xs: '1 / -1', md: '2 / -1' },
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'repeat(auto-fit, minmax(220px, 1fr))',
              },
              gap: { xs: 0.9, md: 1.05 },
              alignItems: 'stretch',
            }}
          >
            <BookingActionGroup title="View" columns="auto" tone="view" minActionWidth={126}>
              {canShowSupportReport ? (
                <BookingActionButton
                  label={supportReportActionLabel}
                  onClick={() => setSupportReportDrawerOpen(true)}
                  tone="report"
                  startIcon={<EventIcon sx={{ fontSize: 17 }} />}
                />
              ) : null}
              {canShowPrescription ? (
                <BookingActionButton label="Prescription" onClick={handlePrescription} tone="report" />
              ) : null}
              {canShowScreeningReport ? (
                <BookingActionButton label="Screening Report" onClick={handleScreeningReport} tone="report" />
              ) : null}
              {canShowTherapyReport ? (
                <BookingActionButton
                  label="Therapy Report"
                  onClick={handleTherapyReport}
                  disabled={!hasSessionStarted}
                  tone="report"
                  title={
                    !hasSessionStarted
                      ? 'Session has not started yet, so no therapy report is available.'
                      : ''
                  }
                  style={
                    !hasSessionStarted
                      ? {
                          color: '#64748b',
                          borderColor: 'rgba(148, 163, 184, 0.5)',
                          backgroundColor: 'rgba(148, 163, 184, 0.14)',
                        }
                      : {}
                  }
                />
              ) : null}
              {canShowPatientHistory ? (
                <BookingActionButton label="Patient History" onClick={handlePatientHistory} tone="history" />
              ) : null}
              {canShowPaymentLinks && !isSessionBlocked ? (
                <BookingActionButton label="Payment Links" onClick={handlePaymentLinks} tone="payment" />
              ) : null}
              {canShowBookingLogs && isSupportPortal ? (
                <BookingActionButton
                  label="Participant Logs"
                  onClick={handleBookingLogs}
                  tone="logs"
                  startIcon={<ExitToAppIcon sx={{ fontSize: 17 }} />}
                />
              ) : null}
              {canShowSupportEngagementInView ? (
                <BookingActionButton
                  label="Engagement"
                  onClick={() => onEngagementClick(bookedEvent)}
                  tone="engagement"
                  startIcon={<VisibilityIcon sx={{ fontSize: 17 }} />}
                />
              ) : null}
              {canShowBookingLogs && !isSupportPortal ? (
                <BookingActionLink label="Participant Logs" onClick={handleBookingLogs} />
              ) : null}
              {customViewActionItemList}
            </BookingActionGroup>

            {hasBottomManageSessionActions ? (
              <BookingActionGroup title="Manage Session" columns={2} tone="manage">
              {canShowManageActions ? (
                <BookingActionButton label={rescheduleActionLabel} onClick={handleReschedule} tone="schedule" />
              ) : null}
              {canShowManageActions ? (
                <BookingActionButton label={cancelActionLabel} onClick={handleCancel} color="error" tone="danger" />
              ) : null}
              {canShowBottomEngagement ? (
                <BookingActionButton
                  label="Engagement"
                  onClick={() => onEngagementClick(bookedEvent)}
                  tone="engagement"
                />
              ) : null}
              </BookingActionGroup>
            ) : null}
            {customBottomActionItems}
          </Box>
        ) : null}

        {checkInPromptOpen && dialogRoomData && clinicLocationData && (
          <CheckInDialogComponent
            closeFunction={() => {
              setCheckInPromptOpen(false)
              setDialogRoomData(null)
              pendingActionAfterCheckInRef.current = null
            }}
            roomData={dialogRoomData} // Use stored room data to prevent unmounting during refetch
            clinicLocationData={clinicLocationData}
            disableTimeout={true} // Disable auto-close timeout for manage-my-bookings page
            onChoosingInPerson={async () => {
              setCheckInPromptOpen(false)
              setDialogRoomData(null)
              executePendingAction()
            }}
            onChoosingRemote={async () => {
              setCheckInPromptOpen(false)
              setDialogRoomData(null)
              executePendingAction()
            }}
          />
        )}

        <CheckoutPrompt
          open={checkoutPromptOpen}
          onClose={() => setCheckoutPromptOpen(false)}
          onCheckout={handleCheckout}
          checkingOut={checkingIn}
        />

        <SupportReportViewDrawer
          open={supportReportDrawerOpen}
          onClose={() => setSupportReportDrawerOpen(false)}
          bookedEvent={bookedEvent}
        />
      </>
    )
  }

  function renderRightSideCustomComponent(bookedEvent) {
    const defaultActions = !hideAllActions ? <BookedEventActions bookedEvent={bookedEvent} /> : null

    if (rightSideCustomComponent) {
      const customContent = rightSideCustomComponent({ event: bookedEvent, defaultActions })
      return customContent ?? defaultActions
    }

    return defaultActions
  }

  function formatStatusValue(value) {
    if (!value) return ''

    return String(value)
      .split(/[-_]/)
      .filter(Boolean)
      .map((statusPart) => capitalize(statusPart))
      .join(' ')
  }

  function getRelativeSessionDateMeta(sessionStart) {
    if (!sessionStart) {
      return {
        sessionDateLabel: 'Date not recorded',
        dayLabel: 'Date',
        datePrimaryLabel: '--',
        dateSecondaryLabel: 'Not set',
      }
    }

    const todayStart = dayjs().startOf('day')
    const sessionDayStart = sessionStart.startOf('day')
    const dayDifference = sessionDayStart.diff(todayStart, 'day')
    const relativeDateLabel =
      dayDifference === 0
        ? 'Today'
        : dayDifference === 1
        ? 'Tomorrow'
        : dayDifference === -1
        ? 'Yesterday'
        : ''

    return {
      sessionDateLabel: relativeDateLabel || sessionStart.format('dddd, D MMMM YYYY'),
      dayLabel: sessionStart.format('ddd'),
      datePrimaryLabel: relativeDateLabel || sessionStart.format('D'),
      dateSecondaryLabel: relativeDateLabel
        ? sessionStart.format('D MMM YYYY')
        : sessionStart.format('MMM YYYY'),
    }
  }

  function getMeetingModeMeta(bookedEvent) {
    const isVideoCall = bookedEvent?.meetingSrc === 'aumhum'

    return {
      isVideoCall,
      meetingModeLabel: isVideoCall ? 'Video Call' : 'In-Clinic',
      MeetingModeIcon: isVideoCall ? VideoChatIcon : WeekendIcon,
    }
  }

  function getBookingStatusLabel(bookedEvent, isPaymentPending, isCancelled) {
    if (isCancelled) return 'Booking Cancelled'
    if (isPaymentPending) return 'Payment Pending'
    if (bookedEvent?.status === BOOKING_STATUSES.BOOKED) return 'Booking Confirmed'
    if (bookedEvent?.status === BOOKING_STATUSES.BOOKING_INITIATED) return 'Booking Initiated'

    return formatStatusValue(bookedEvent?.status) || 'Booked'
  }

  function getSessionStatusLabel(bookedEvent, isCancelled) {
    if (isCancelled) return 'Cancelled'
    if (bookedEvent?.markedCompleted || bookedEvent?.sessionStatus === 'marked-completed') {
      return 'Completed'
    }
    if (bookedEvent?.sessionStatus === 'in-progress') return 'In Progress'
    if (bookedEvent?.sessionStatus) return formatStatusValue(bookedEvent?.sessionStatus)

    return 'Not Started'
  }

  function isBookedEventCompletedForActions(bookedEvent) {
    return Boolean(
      bookedEvent?.markedCompleted ||
        bookedEvent?.sessionStatus === BOOKING_SESSION_STATUS.MARKED_COMPLETED ||
        bookedEvent?.sessionStatus === BOOKING_SESSION_STATUS.FINISHED,
    )
  }

  function formatSessionDuration(totalMinutes) {
    const safeTotalMinutes = Math.max(0, Math.floor(totalMinutes || 0))
    const days = Math.floor(safeTotalMinutes / 1440)
    const hours = Math.floor((safeTotalMinutes % 1440) / 60)
    const minutes = safeTotalMinutes % 60

    if (days > 0) {
      return `${days} ${days === 1 ? 'day' : 'days'}`
    }

    if (hours > 0) {
      const minutesLabel = minutes > 0 ? ` ${minutes} ${minutes === 1 ? 'min' : 'mins'}` : ''
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'}${minutesLabel}`
    }

    if (minutes > 0) {
      return `${minutes} ${minutes === 1 ? 'min' : 'mins'}`
    }

    return 'less than 1 min'
  }

  function getBookedEventCompletionTime(bookedEvent, sessionEnd) {
    const completionTime =
      bookedEvent?.markedCompletedAt ||
      bookedEvent?.completedAt ||
      bookedEvent?.sessionCompletedAt ||
      bookedEvent?.completionTime ||
      bookedEvent?.markedCompletedTime ||
      bookedEvent?.finishedAt

    if (completionTime) {
      return dayjs.utc(completionTime).local()
    }

    return sessionEnd || null
  }

  function getSessionTimingMeta(bookedEvent, sessionStart, sessionEnd, now) {
    const isCancelled = bookedEvent?.status === STATUS_SESSION_CANCELLED
    const rawSessionStatus =
      bookedEvent?.sessionStatus ||
      (bookedEvent?.markedCompleted
        ? BOOKING_SESSION_STATUS.MARKED_COMPLETED
        : BOOKING_SESSION_STATUS.NOT_STARTED)
    const isCompleted =
      bookedEvent?.markedCompleted || rawSessionStatus === BOOKING_SESSION_STATUS.MARKED_COMPLETED
    const isInProgress = rawSessionStatus === BOOKING_SESSION_STATUS.IN_PROGRESS
    const isNeverStarted = rawSessionStatus === BOOKING_SESSION_STATUS.NOT_STARTED

    if (isCancelled) {
      return {
        title: 'Cancelled Session',
        detail: 'Booking cancelled',
        displayLabel: 'Cancelled Session',
        shouldEmphasize: false,
      }
    }

    if (isCompleted) {
      const completionTime = getBookedEventCompletionTime(bookedEvent, sessionEnd)
      const completionMinutesAgo = completionTime ? now.diff(completionTime, 'minute') : null
      const detail =
        completionMinutesAgo !== null && completionMinutesAgo >= 0
          ? `${formatSessionDuration(completionMinutesAgo)} ago`
          : ''

      return {
        title: 'Completed',
        detail,
        displayLabel: detail ? `Completed ${detail}` : 'Completed',
        shouldEmphasize: false,
      }
    }

    if (isInProgress) {
      const minutesSinceStart = sessionStart ? now.diff(sessionStart, 'minute') : null
      const isStarted = minutesSinceStart !== null && minutesSinceStart >= 0

      if (isStarted && minutesSinceStart >= 1440) {
        return {
          title: 'Started but not completed',
          detail: `${formatSessionDuration(minutesSinceStart)} ago`,
          displayLabel: 'Started but not completed',
          shouldEmphasize: false,
        }
      }

      if (isStarted) {
        return {
          title: 'In Progress',
          detail: `for ${formatSessionDuration(minutesSinceStart)}`,
          displayLabel: `In Progress for ${formatSessionDuration(minutesSinceStart)}`,
          shouldEmphasize: minutesSinceStart <= 60,
          attentionTone: 'in-progress',
        }
      }

      return {
        title: 'In Progress',
        detail: '',
        displayLabel: 'In Progress',
        shouldEmphasize: false,
      }
    }

    if (sessionStart) {
      const minutesUntilStart = sessionStart.diff(now, 'minute')

      if (minutesUntilStart >= 0) {
        const timeUntilStartLabel = formatSessionDuration(minutesUntilStart)

        return {
          title: isNeverStarted ? 'Never Started' : 'Starts in',
          detail: isNeverStarted ? `Starts in ${timeUntilStartLabel}` : timeUntilStartLabel,
          displayLabel: isNeverStarted
            ? `Never Started - starts in ${timeUntilStartLabel}`
            : `Starts in ${timeUntilStartLabel}`,
          shouldEmphasize: minutesUntilStart <= 180,
          attentionTone: 'upcoming',
        }
      }

      const minutesSinceStart = Math.abs(minutesUntilStart)
      const timeSinceStartLabel = formatSessionDuration(minutesSinceStart)
      return {
        title: isNeverStarted ? 'Never Started' : 'Start time passed',
        detail: isNeverStarted
          ? `Start time passed ${timeSinceStartLabel} ago`
          : `${timeSinceStartLabel} ago`,
        displayLabel: isNeverStarted
          ? `Never Started - start time passed ${timeSinceStartLabel} ago`
          : `Start time passed ${timeSinceStartLabel} ago`,
        shouldEmphasize: false,
      }
    }

    const fallbackStatusLabel = getSessionStatusLabel(bookedEvent, isCancelled)
    return {
      title: fallbackStatusLabel,
      detail: '',
      displayLabel: fallbackStatusLabel,
      shouldEmphasize: false,
    }
  }

  return (
    <>
      {!hideRefreshButton && (
        <GenericDisplayFlex sb style={{ flexWrap: 'wrap', padding: '20px', paddingBottom: '0' }}>
          <GenericDisplayFlex style={{ flexWrap: 'wrap', gap: '20px' }}>
            {/* <NewEventTypeButton
            selected={bookedEventsSelectedView === 'upcoming'}
            MRZERO
            onClick={() => setBookedEventsSelectedView('upcoming')}
          >
            Active/ Upcoming
          </NewEventTypeButton>
          <NewEventTypeButton
            selected={bookedEventsSelectedView === 'past'}
            onClick={() => setBookedEventsSelectedView('past')}
          >
            Past
          </NewEventTypeButton> */}
          </GenericDisplayFlex>
          {loadingSlots ? (
            <Spinner animation="border" variant="dark" size="sm" />
          ) : (
            <Tooltip title={<strong>Refresh Bookings</strong>}>
              <StyledEventsRefreshIcon onClick={refreshEvents} />
            </Tooltip>
          )}
        </GenericDisplayFlex>
      )}
      <EventsPageMainItemsContainer>
        {allEvents?.map((bookedEvent, index) => {
          const matchedEvent = allEvents?.find((eventItem) => eventFindLogicOverride(eventItem, bookedEvent))
          const patientName = bookedEvent?.patientInfoData?.name || bookedEvent?.userName || 'Participant'
          const patientAgeGenderLabel = bookedEvent?.patientInfoData?.dateOfBirth
            ? `${getAgeFromDobWithTime(bookedEvent?.patientInfoData?.dateOfBirth)} Y | ${capitalize(bookedEvent?.patientInfoData?.gender)}`
            : capitalize(bookedEvent?.patientInfoData?.gender || '')
          const shouldHidePatientContactDetails =
            hidePatientContactDetails || bookedEvent?.patientContactDetailsVisible === false
          const patientPhoneLabel = !shouldHidePatientContactDetails && bookedEvent?.patientInfoData?.mobile
            ? formatPhoneNumber(bookedEvent?.patientInfoData?.mobile)?.concatenatedString || bookedEvent?.patientInfoData?.mobile
            : ''
          const sessionStart = bookedEvent?.startTime ? dayjs?.utc(bookedEvent?.startTime).local() : null
          const sessionEnd = bookedEvent?.endTime ? dayjs?.utc(bookedEvent?.endTime).local() : null
          const cardNow = dayjs(currentTime)
          const sessionDateMeta = getRelativeSessionDateMeta(sessionStart)
          const sessionStartLabel = sessionStart ? sessionStart?.format('h:mma') : 'Time not set'
          const sessionEndLabel = sessionEnd ? sessionEnd?.format('h:mma') : ''
          const eventTypeLabel = displayNamesOfEventTypes?.[bookedEvent?.eventType] || 'Session'
          const explicitEventTitle =
            bookedEvent?.eventName ||
            bookedEvent?.eventTitle ||
            bookedEvent?.eventTypeTitle ||
            matchedEvent?.title ||
            ''
          const eventTitleLabel = explicitEventTitle || eventTypeLabel
          const explicitDiscipline =
            bookedEvent?.discipline ||
            bookedEvent?.therapyDiscipline ||
            matchedEvent?.discipline ||
            matchedEvent?.therapyDiscipline ||
            ''
          const disciplineDisplayLabel = explicitDiscipline
            ? therapyDisciplineLabel(explicitDiscipline)
            : ''
          const patientContextChipLabel = disciplineDisplayLabel || eventTitleLabel || eventTypeLabel
          const clinicSummaryLabel =
            bookedEvent?.clinicBrandData?.brandName ||
            bookedEvent?.clinicBrandData?.title ||
            'Brand not recorded'
          const locationSummaryLabel =
            bookedEvent?.clinicLocation?.title ||
            bookedEvent?.clinicLocation?.name ||
            'Location not recorded'
          const { meetingModeLabel, MeetingModeIcon } = getMeetingModeMeta(bookedEvent)
          const isPaymentPending =
            bookedEvent?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
            bookedEvent?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
          const isCancelled = bookedEvent?.status === STATUS_SESSION_CANCELLED
          const isRescheduled =
            bookedEvent?.modificationHistory?.length > 0 &&
            bookedEvent?.modificationHistory?.some((hist) => hist?.event === 'booking-rescheduled')
          const bookingStatusLabel = getBookingStatusLabel(bookedEvent, isPaymentPending, isCancelled)
          const sessionTimingMeta = getSessionTimingMeta(bookedEvent, sessionStart, sessionEnd, cardNow)
          const bookedSlotColorToken = {
            status: bookedEvent?.status,
            sessionStatus: bookedEvent?.sessionStatus,
            markedCompleted: bookedEvent?.markedCompleted,
            visitEventType: bookedEvent?.visitEventType,
            paymentStatus: bookedEvent?.paymentStatus,
          }
          const bookedSlotColors = getBookedSlotStripColor(bookedSlotColorToken, false, true)
          const displaySlotColors = isCancelled
            ? {
                ...bookedSlotColors,
                background: '#fee2e2',
                borderColor: '#dc2626',
                fontColor: '#7f1d1d',
                leftStripBorderColor: '#991b1b',
                visitTextColor: '#7f1d1d',
              }
            : bookedSlotColors
          const statusAccentColor = displaySlotColors?.borderColor || '#6366f1'
          const statusAccentInk =
            displaySlotColors?.visitTextColor || displaySlotColors?.fontColor || '#0f172a'
          const statusAccentBorder = alpha(statusAccentColor, 0.28)
          const statusAccentSurface = alpha(displaySlotColors?.background || statusAccentColor, 0.42)
          const statusAccentTint = alpha(statusAccentColor, 0.1)
          const statusAccentShadow = alpha(statusAccentColor, 0.18)
          const practitionerProfilePicId =
            bookedEvent?.mentorProfilePicId ||
            bookedEvent?.mentorData?.profilePicId ||
            bookedEvent?.mentorProfileData?.profilePicId ||
            bookedEvent?.profilePicId ||
            ''
          const practitionerAvatarSrc = practitionerProfilePicId
            ? getImageLink(practitionerProfilePicId)
            : undefined
          const bookingMetaChipSx = {
            height: 24,
            bgcolor: 'rgba(255, 255, 255, 0.82)',
            color: '#475569',
            border: '1px solid rgba(148, 163, 184, 0.34)',
            fontWeight: 750,
          }
          const bookingStatusChipSx = isCancelled
            ? {
                ...bookingMetaChipSx,
                bgcolor: 'rgba(254, 226, 226, 0.82)',
                color: '#991b1b',
                border: '1px solid rgba(220, 38, 38, 0.34)',
              }
            : bookingMetaChipSx

          return (
            <BookedEventContainer 
              key={`card-${bookedEvent?.id || index}`}
              data-card-index={index}
              sessionStatus={bookedEvent?.sessionStatus}
              markedCompleted={bookedEvent?.markedCompleted}
              status={bookedEvent?.status}
              visitEventType={bookedEvent?.visitEventType}
              paymentStatus={bookedEvent?.paymentStatus}
              $attention={sessionTimingMeta.shouldEmphasize}
              $attentionTone={sessionTimingMeta.attentionTone}
              $statusBorderColor={statusAccentBorder}
              $statusSurfaceColor={statusAccentSurface}
              $statusShadowColor={statusAccentShadow}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isSupportBookingCardLayout
                    ? {
                        xs: '82px minmax(0, 1fr)',
                        md: '98px minmax(0, 1fr)',
                        lg: '98px minmax(0, 1fr) minmax(300px, 356px)',
                        xl: '104px minmax(0, 1fr) minmax(328px, 390px)',
                      }
                    : {
                        xs: '82px minmax(0, 1fr)',
                        md: '98px minmax(0, 1fr) 286px',
                        xl: '104px minmax(0, 1fr) 304px',
                      },
                  gap: { xs: 1, sm: 1.25, lg: 1.35 },
                  alignItems: 'stretch',
                  width: '100%',
                }}
              >
                <Box sx={{ minWidth: 0, gridRow: { md: '1 / span 2' }, alignSelf: 'stretch' }}>
                  <SessionTimeRail
                    dayLabel={sessionDateMeta.dayLabel}
                    datePrimaryLabel={sessionDateMeta.datePrimaryLabel}
                    dateSecondaryLabel={sessionDateMeta.dateSecondaryLabel}
                    startLabel={sessionStartLabel}
                    endLabel={sessionEndLabel}
                    statusLabel={sessionTimingMeta.title}
                    statusHelperLabel={sessionTimingMeta.detail}
                    MeetingModeIcon={MeetingModeIcon}
                    colors={displaySlotColors}
                    isCancelled={isCancelled}
                  />
                </Box>

                <Box 
                  sx={{
                    minWidth: 0,
                    py: { xs: 0.25, sm: 0.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.1,
                  }}
                >
                  <Stack
                    direction={isSupportBookingCardLayout ? { xs: 'column', lg: 'row' } : { xs: 'column', md: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'flex-start' }}
                  >
                    <Box
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        p: { xs: 1, sm: 1.15 },
                        borderRadius: '8px',
                        border: `1px solid ${statusAccentBorder}`,
                        background: `linear-gradient(135deg, ${statusAccentSurface} 0%, rgba(255, 255, 255, 0.95) 72%)`,
                        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      <Stack direction="row" spacing={1.05} alignItems="flex-start" sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: '8px',
                            flexShrink: 0,
                            display: 'grid',
                            placeItems: 'center',
                            background: statusAccentTint,
                            color: statusAccentInk,
                          }}
                        >
                          <PersonIcon sx={{ fontSize: 25 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, flexWrap: 'wrap' }}>
                            <Typography
                              sx={{
                                fontSize: { xs: 20, sm: 23 },
                                fontWeight: 950,
                                color: statusAccentInk,
                                lineHeight: 1.12,
                                wordBreak: 'break-word',
                              }}
                            >
                              {patientName}
                            </Typography>
                            <Chip
                              size="small"
                              icon={<EventIcon sx={{ fontSize: 15 }} />}
                              label={patientContextChipLabel}
                              sx={{
                                height: 25,
                                maxWidth: { xs: '100%', sm: 240 },
                                bgcolor: statusAccentTint,
                                color: statusAccentInk,
                                border: `1px solid ${statusAccentBorder}`,
                                fontWeight: 800,
                                '& .MuiChip-label': {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                },
                                '& .MuiChip-icon': {
                                  color: 'inherit',
                                },
                              }}
                            />
                            {bookedEvent?.visitEventType === VISIT_EVENT_TYPES.emergency && (
                              <Tooltip title="Emergency Session">
                                <LocalHospitalIcon
                                  sx={{
                                    color: '#ef4444',
                                    fontSize: 22,
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>

                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                            {bookedEvent?.patientInfoData?.patientNumber ? (
                              <Chip
                                label={`Patient #${bookedEvent.patientInfoData.patientNumber}`}
                                size="small"
                                sx={{
                                  height: 24,
                                  bgcolor: 'rgba(255, 255, 255, 0.78)',
                                  color: '#475569',
                                  border: '1px solid rgba(148, 163, 184, 0.34)',
                                  fontWeight: 750,
                                }}
                              />
                            ) : null}
                            {patientAgeGenderLabel ? (
                              <Chip
                                label={patientAgeGenderLabel}
                                size="small"
                                sx={{
                                  height: 24,
                                  bgcolor: 'rgba(255, 255, 255, 0.78)',
                                  color: '#475569',
                                  border: '1px solid rgba(148, 163, 184, 0.34)',
                                  fontWeight: 700,
                                }}
                              />
                            ) : null}
                            {patientPhoneLabel ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                icon={<PhoneIcon sx={{ fontSize: 14 }} />}
                                label={patientPhoneLabel}
                                sx={{
                                  height: 24,
                                  bgcolor: 'rgba(255, 255, 255, 0.78)',
                                  borderColor: 'rgba(148, 163, 184, 0.34)',
                                  color: '#475569',
                                  fontWeight: 700,
                                }}
                              />
                            ) : null}
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>

                    {showMentorName ? (
                      <Box
                        sx={{
                          width: { xs: '100%', lg: 292 },
                          flexShrink: 0,
                          p: { xs: 0.95, sm: 1.05 },
                          borderRadius: '8px',
                          border: '1px solid rgba(148, 163, 184, 0.28)',
                          background: 'rgba(255, 255, 255, 0.76)',
                          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.68)',
                        }}
                      >
                        <Stack direction="row" spacing={0.95} alignItems="center" sx={{ minWidth: 0 }}>
                          <Avatar
                            src={practitionerAvatarSrc}
                            sx={{
                              width: 38,
                              height: 38,
                              bgcolor: statusAccentTint,
                              color: statusAccentInk,
                              border: `1px solid ${statusAccentBorder}`,
                              fontSize: 15,
                              fontWeight: 900,
                            }}
                          >
                            {bookedEvent.mentorName?.charAt(0) || 'P'}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                display: 'block',
                                fontWeight: 850,
                                letterSpacing: 0,
                                lineHeight: 1.15,
                              }}
                            >
                              Practitioner
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#0f172a',
                                fontWeight: 900,
                                lineHeight: 1.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={bookedEvent.mentorName || 'Practitioner not assigned'}
                            >
                              {bookedEvent.mentorName || 'Practitioner not assigned'}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mt: 0.8 }}>
                          {bookedEvent?.patientMeetingSeqNumber ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Visit #${bookedEvent.patientMeetingSeqNumber}`}
                              sx={{
                                height: 24,
                                bgcolor: 'rgba(255, 255, 255, 0.82)',
                                color: '#475569',
                                border: '1px solid rgba(148, 163, 184, 0.34)',
                                fontWeight: 750,
                              }}
                            />
                          ) : null}
                          <Chip
                            size="small"
                            label={bookingStatusLabel}
                            sx={{
                              height: 24,
                              bgcolor: 'rgba(255, 255, 255, 0.82)',
                              color: '#475569',
                              border: '1px solid rgba(148, 163, 184, 0.34)',
                              fontWeight: 750,
                            }}
                          />
                          {bookedEvent?.visitEventType === VISIT_EVENT_TYPES.emergency ? (
                            <Chip size="small" color="error" label="Emergency" sx={{ height: 24, fontWeight: 700 }} />
                          ) : null}
                          {isRescheduled ? (
                            <Chip size="small" color="info" label="Rescheduled" sx={{ height: 24, fontWeight: 700 }} />
                          ) : null}
                        </Stack>
                      </Box>
                    ) : null}
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: isSupportBookingCardLayout
                        ? {
                            xs: '1fr',
                            lg: 'minmax(0, 1.45fr) minmax(220px, 0.9fr)',
                          }
                        : {
                            xs: '1fr',
                            md: 'minmax(0, 1.45fr) minmax(220px, 0.9fr)',
                          },
                      gap: { xs: 0.9, md: 1.05 },
                      pt: 0.25,
                    }}
                  >
                    <BookingFactRow
                      icon={<MeetingModeIcon sx={{ fontSize: 17 }} />}
                      label={`${meetingModeLabel} Session`}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: '#0f172a', fontWeight: 800, lineHeight: 1.35, wordBreak: 'break-word' }}
                      >
                        {eventTitleLabel}
                      </Typography>
                      {eventTitleLabel !== eventTypeLabel ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.3, lineHeight: 1.35 }}
                        >
                          Type: {eventTypeLabel}
                        </Typography>
                      ) : null}
                    </BookingFactRow>

                    <BookingFactRow
                      icon={<BusinessIcon sx={{ fontSize: 17 }} />}
                      label="Clinic"
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: '#0f172a', fontWeight: 800, lineHeight: 1.35, wordBreak: 'break-word' }}
                      >
                        {clinicSummaryLabel}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.35, lineHeight: 1.35, wordBreak: 'break-word' }}
                      >
                        {locationSummaryLabel}
                      </Typography>
                    </BookingFactRow>
                  </Box>

                  {!showMentorName ? (
                    <Stack
                      direction="row"
                      spacing={0.6}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: -0.1 }}
                    >
                      {bookedEvent?.patientMeetingSeqNumber ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Visit #${bookedEvent.patientMeetingSeqNumber}`}
                          sx={bookingMetaChipSx}
                        />
                      ) : null}
                      <Chip
                        size="small"
                        label={bookingStatusLabel}
                        sx={bookingStatusChipSx}
                      />
                      {bookedEvent?.visitEventType === VISIT_EVENT_TYPES.emergency ? (
                        <Chip size="small" color="error" label="Emergency" sx={{ height: 24, fontWeight: 700 }} />
                      ) : null}
                      {isRescheduled ? (
                        <Chip size="small" color="info" label="Rescheduled" sx={{ height: 24, fontWeight: 700 }} />
                      ) : null}
                    </Stack>
                  ) : null}

                  {bookedEvent?.blockedNextVisitSlot &&
                    !!bookedEvent?.blockedNextVisitSlot?.startTime && (
                      <Box
                        sx={{
                          px: 1.1,
                          py: 0.9,
                          borderRadius: '8px',
                          border: '1px solid rgba(16, 185, 129, 0.22)',
                          background: 'linear-gradient(135deg, rgba(236, 253, 245, 0.96) 0%, rgba(240, 253, 250, 0.9) 100%)',
                        }}
                      >
                        <Stack direction="row" spacing={0.8} alignItems="center">
                          <CalendarTodayIcon sx={{ fontSize: 16, color: '#10b981' }} />
                          <Typography variant="body2" sx={{ color: '#047857', fontWeight: 700, lineHeight: 1.4 }}>
                            Next visit:{' '}
                            {dayjs
                              ?.utc(bookedEvent?.blockedNextVisitSlot?.startTime)
                              .local()
                              ?.format('dddd, D MMMM YYYY, h:mmA')}
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                </Box>

                {renderRightSideCustomComponent(bookedEvent)}
              </Box>
            </BookedEventContainer>
          )
        })}

        {allEvents?.length === 0 && <BookedEventTitle>No bookings</BookedEventTitle>}
      </EventsPageMainItemsContainer>
      <ReportViewDrawer
        open={reportDrawerState.open}
        onClose={closeReportDrawer}
        bookedEvent={reportDrawerState.bookedEvent}
        reportType={reportDrawerState.reportType}
      />
      <Dialog
        open={showPaymentLinksDialog}
        maxWidth="lg"
        fullWidth
        // PaperComponent={StyledRequestTherapyComponent}
        // PaperProps={{
        //   onKeyDown: handleKeyDown, // Prevent Escape key from closing it
        // }}
        onClose={closePaymentLinkPopup}
      >
        <PaymentLinksViewer
          bookedSlot={selectedBooking}
          closeDialog={closePaymentLinkPopup}
          refreshBookedSlots={refreshEvents}
        />
      </Dialog>
      <Dialog
        open={pastReportsDialog}
        PaperComponent={PastTherapyReportDialogPaper}
        onClose={closePastReportsDialog}
      >
        <PastTherapyReportDialog
          bookedEvent={selectedBooking}
          closeDialog={closePastReportsDialog}
        />
      </Dialog>
      {cancelDialogData && (
        <ConfirmCancelSessionDialog
          open={cancelDialogData}
          closeFunc={() => setCancelDialogData(null)}
          sessionData={cancelDialogData}
          afterCancelSuccess={refreshEvents}
          cancelAppointmentApiSignature={cancelAppointmentApiSignature}
        />
      )}
    </>
  )
}

export const ConfirmCancelSessionDialog = ({
  open,
  closeFunc,
  sessionData,
  afterCancelSuccess,
  cancelAppointmentApiSignature,
}) => {
  const [cancelLoading, setCancelLoading] = useState(false)
  const [reason, setReason] = useState('')
  const [refundType, setRefundType] = useState('none')
  const [submitError, setSubmitError] = useState('')

  async function cancelSession() {
    if (!reason.trim()) {
      // Reason is optional, but we can show a warning or allow empty
      // For now, we'll allow empty reason
    }

    setSubmitError('')
    setCancelLoading(true)
    const response = await cancelBookedSession(
      cancelAppointmentApiSignature, 
      sessionData?.id,
      reason.trim() || null,
      refundType,
      sessionData?.slug // bookedEventSlug
    )
    setCancelLoading(false)
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      if (afterCancelSuccess) {
        afterCancelSuccess()
      }
      if (closeFunc) {
        closeFunc()
      }
      // Reset form
      setReason('')
      setRefundType('none')
      setSubmitError('')
      return
    }

    const errorMessage = buildSupportBookedEventActionErrorMessage(
      response,
      'Failed to cancel booking',
    )
    setSubmitError(errorMessage)
    toast.error(errorMessage)
  }

  const handleClose = () => {
    setReason('')
    setRefundType('none')
    setSubmitError('')
    if (closeFunc) {
      closeFunc()
    }
  }

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      PaperComponent={BootStrapDialogPaperComponent}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: 0,
          maxWidth: '520px',
        },
      }}
    >
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: 700, 
            color: 'rgba(0, 0, 0, 0.85)',
            margin: '0 0 8px 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Cancel Appointment
          </h2>
          <p style={{ 
            fontSize: '14px', 
            color: 'rgba(0, 0, 0, 0.6)',
            margin: 0,
            lineHeight: 1.5
          }}>
            Please provide a reason for cancellation and specify refund preference.
          </p>
        </div>
        
        <div style={{ 
          marginBottom: '20px',
          padding: '16px',
          background: 'rgba(67, 56, 202, 0.04)',
          borderRadius: '12px',
          border: '1px solid rgba(67, 56, 202, 0.1)'
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 600,
            color: 'rgba(0, 0, 0, 0.85)'
          }}>
            Patient: {sessionData?.userName || 'N/A'}
          </div>
          <div style={{ 
            fontSize: '13px',
            color: 'rgba(0, 0, 0, 0.7)'
          }}>
            {sessionData?.startTime && sessionData?.endTime && (
              <>
                {dayjs.utc(sessionData.startTime).local().format('DD MMM YYYY, h:mm A')} - {' '}
                {dayjs.utc(sessionData.endTime).local().format('h:mm A')}
              </>
            )}
          </div>
        </div>

        {submitError ? (
          <div
            style={{
              marginBottom: '20px',
              padding: '12px 14px',
              borderRadius: '10px',
              background: 'rgba(254, 242, 242, 0.95)',
              border: '1px solid rgba(248, 113, 113, 0.25)',
              color: '#b91c1c',
              fontSize: '13px',
              lineHeight: 1.5,
              fontWeight: 500,
            }}
          >
            {submitError}
          </div>
        ) : null}

        {/* Reason Field */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block',
            fontSize: '13px',
            fontWeight: 600,
            color: 'rgba(0, 0, 0, 0.85)',
            marginBottom: '8px'
          }}>
            Reason for Cancellation <span style={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: 400 }}>(Optional)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for cancellation..."
            disabled={cancelLoading}
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid rgba(0, 0, 0, 0.2)',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
              transition: 'border-color 0.2s ease',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'rgba(67, 56, 202, 0.5)'
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)'
            }}
          />
        </div>

        {/* Refund Type Selection */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block',
            fontSize: '13px',
            fontWeight: 600,
            color: 'rgba(0, 0, 0, 0.85)',
            marginBottom: '12px'
          }}>
            Refund Recommendation
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: cancelLoading ? 'not-allowed' : 'pointer',
              padding: '12px',
              borderRadius: '8px',
              border: `2px solid ${refundType === 'none' ? 'rgba(67, 56, 202, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
              background: refundType === 'none' ? 'rgba(67, 56, 202, 0.05)' : 'transparent',
              transition: 'all 0.2s ease',
            }}>
              <input
                type="radio"
                name="refundType"
                value="none"
                checked={refundType === 'none'}
                onChange={(e) => setRefundType(e.target.value)}
                disabled={cancelLoading}
                style={{ cursor: cancelLoading ? 'not-allowed' : 'pointer' }}
              />
              <div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(0, 0, 0, 0.85)' }}>
                  No Refund
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.6)', marginTop: '2px' }}>
                  Patient will not receive a refund
                </div>
              </div>
            </label>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: cancelLoading ? 'not-allowed' : 'pointer',
              padding: '12px',
              borderRadius: '8px',
              border: `2px solid ${refundType === 'full' ? 'rgba(67, 56, 202, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
              background: refundType === 'full' ? 'rgba(67, 56, 202, 0.05)' : 'transparent',
              transition: 'all 0.2s ease',
            }}>
              <input
                type="radio"
                name="refundType"
                value="full"
                checked={refundType === 'full'}
                onChange={(e) => setRefundType(e.target.value)}
                disabled={cancelLoading}
                style={{ cursor: cancelLoading ? 'not-allowed' : 'pointer' }}
              />
              <div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(0, 0, 0, 0.85)' }}>
                  Full Refund
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.6)', marginTop: '2px' }}>
                  Recommend full refund to the patient
                </div>
              </div>
            </label>
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
          marginTop: '24px'
        }}>
          <button
            onClick={handleClose}
            disabled={cancelLoading}
            style={{ 
              padding: '10px 24px',
              borderRadius: '10px',
              border: '1px solid rgba(0, 0, 0, 0.2)',
              background: '#fff',
              color: 'rgba(0, 0, 0, 0.85)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: cancelLoading ? 'not-allowed' : 'pointer',
              opacity: cancelLoading ? 0.5 : 1,
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => {
              if (!cancelLoading) {
                e.target.style.background = 'rgba(0, 0, 0, 0.05)'
              }
            }}
            onMouseOut={(e) => {
              e.target.style.background = '#fff'
            }}
          >
            Keep Appointment
          </button>
          <button
            onClick={cancelSession}
            disabled={cancelLoading}
            style={{ 
              padding: '10px 24px',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%)',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: cancelLoading ? 'not-allowed' : 'pointer',
              opacity: cancelLoading ? 0.7 : 1,
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onMouseOver={(e) => {
              if (!cancelLoading) {
                e.target.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 1) 0%, rgba(220, 38, 38, 1) 100%)'
                e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.35)'
                e.target.style.transform = 'translateY(-1px)'
              }
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%)'
              e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.25)'
              e.target.style.transform = 'translateY(0)'
            }}
          >
            {cancelLoading ? (
              <>
                <Spinner animation="border" variant="light" size="sm" />
                Cancelling...
              </>
            ) : (
              'Cancel Appointment'
            )}
          </button>
        </div>
      </div>
    </BootstrapDialog>
  )
}

const BootstrapDialog = muiStyled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}))

const BootStrapDialogPaperComponent = muiStyled(Paper)(
  ({ theme }) => `
    max-width: unset !important;
`,
)
